import React, { useState, useEffect, useCallback } from 'react';
import { maskAIModel } from '../utils/aiBrand';
import { Brain, Save, RefreshCw, GitBranch, Cpu, AlertTriangle, Target, Shield, Zap, Loader2, CheckCircle, XCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const LLMLogicConfig = () => {
  const [config, setConfig] = useState({
    // Core LLM Settings
    reasoning_model: '',
    action_model: '',
    enable_chain_of_thought: true,
    enable_self_reflection: true,

    // Pentest Configuration
    max_iterations: 10,
    timeout_per_tool: 300, // seconds
    parallel_execution: true,
    max_parallel_tools: 3,

    // Decision Making
    risk_threshold: 'medium', // low, medium, high
    confidence_threshold: 0.7,
    auto_escalation: false,
    require_human_approval: true,

    // Tool Selection Strategy
    tool_selection: 'adaptive', // sequential, adaptive, aggressive
    prefer_passive_tools: true,
    fallback_on_failure: true,
  });

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ollamaModels, setOllamaModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [modelsError, setModelsError] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);
  const [error, setError] = useState(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
  });

  // Fetch current config from backend
  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE}/api/v1/system/attack-matrix`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.config) {
        // Merge with defaults to ensure all fields exist
        setConfig(prev => ({
          ...prev,
          reasoning_model: data.config.llm_model || prev.reasoning_model,
          action_model: data.config.llm_model || prev.action_model,
          enable_chain_of_thought: data.config.enable_chain_of_thought ?? true,
          enable_self_reflection: data.config.enable_self_reflection ?? true,
          max_iterations: data.config.max_iterations || 10,
          timeout_per_tool: data.config.timeout_per_tool || 300,
          parallel_execution: data.config.parallel_execution ?? true,
          max_parallel_tools: data.config.max_parallel_tools || 3,
          risk_threshold: data.config.risk_threshold || 'medium',
          confidence_threshold: data.config.confidence_threshold || 0.7,
          auto_escalation: data.config.auto_escalation ?? false,
          require_human_approval: data.config.require_human_approval ?? true,
          tool_selection: data.config.tool_selection || 'adaptive',
          prefer_passive_tools: data.config.prefer_passive_tools ?? true,
          fallback_on_failure: data.config.fallback_on_failure ?? true,
        }));
      }
    } catch (err) {
      console.error('Error fetching config:', err);
      setError('Erreur lors du chargement de la configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch Ollama models dynamically
  const fetchOllamaModels = useCallback(async () => {
    try {
      setModelsLoading(true);
      setModelsError(null);

      const response = await fetch(`${API_BASE}/api/v1/llm/ollama-models`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.models) {
        setOllamaModels(data.models);
        // Set default models if not already set
        if (!config.reasoning_model && data.models.length > 0) {
          // Prefer sylink model for reasoning, or first model
          const sylinkModel = data.models.find(m => m.name.toLowerCase().includes('sylink'));
          const defaultReasoning = sylinkModel?.id || data.models[0]?.id || '';
          setConfig(prev => ({
            ...prev,
            reasoning_model: defaultReasoning,
            action_model: defaultReasoning
          }));
        }
      }
    } catch (err) {
      console.error('Error fetching Ollama models:', err);
      setModelsError('Impossible de récupérer les modèles SYLink AI');
    } finally {
      setModelsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchOllamaModels();
  }, [fetchConfig, fetchOllamaModels]);

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);
    setError(null);

    try {
      // Get current attack matrix config first
      const getResponse = await fetch(`${API_BASE}/api/v1/system/attack-matrix`, {
        headers: getAuthHeaders(),
      });

      if (!getResponse.ok) {
        throw new Error('Failed to fetch current config');
      }

      const currentData = await getResponse.json();
      const currentConfig = currentData.config || {};

      // Merge LLM logic settings with existing config
      const updatedConfig = {
        ...currentConfig,
        llm_model: config.reasoning_model,
        enable_chain_of_thought: config.enable_chain_of_thought,
        enable_self_reflection: config.enable_self_reflection,
        max_iterations: config.max_iterations,
        timeout_per_tool: config.timeout_per_tool,
        parallel_execution: config.parallel_execution,
        max_parallel_tools: config.max_parallel_tools,
        risk_threshold: config.risk_threshold,
        confidence_threshold: config.confidence_threshold,
        auto_escalation: config.auto_escalation,
        require_human_approval: config.require_human_approval,
        tool_selection: config.tool_selection,
        prefer_passive_tools: config.prefer_passive_tools,
        fallback_on_failure: config.fallback_on_failure,
      };

      // Save to backend
      const response = await fetch(`${API_BASE}/api/v1/system/attack-matrix`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ config: updatedConfig }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail?.message || errorData.detail || 'Erreur de sauvegarde');
      }

      const data = await response.json();

      if (data.success) {
        setSaveMessage('Configuration LLM sauvegardée avec succès');
        setTimeout(() => setSaveMessage(null), 3000);
      } else {
        throw new Error(data.error || 'Erreur inconnue');
      }
    } catch (err) {
      console.error('Save error:', err);
      setError(`Erreur: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Messages */}
      {saveMessage && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <span className="text-green-400">{saveMessage}</span>
        </div>
      )}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
          <XCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      {/* Header with Save Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Brain className="w-8 h-8 text-brand-green" />
          <div>
            <h3 className="text-xl font-bold text-white">Logique LLM pour Pentests</h3>
            <p className="text-sm text-gray-400">Configuration du raisonnement IA et workflow d'execution</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchConfig}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-brand-black-lighter border border-gray-700 text-gray-300 rounded-lg hover:bg-brand-black transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-brand-green to-brand-green-dark text-brand-black font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-green/30 transition-all disabled:opacity-50"
          >
            {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      {/* Process Flow Diagram */}
      <div className="bg-gradient-to-br from-brand-black via-brand-black-light to-brand-black rounded-xl border-2 border-brand-green/30 p-8">
        <h4 className="text-lg font-semibold text-brand-green mb-6 flex items-center gap-2">
          <GitBranch className="w-5 h-5" />
          Schéma de Processus LLM
        </h4>
        
        <div className="relative">
          {/* Flow Diagram */}
          <div className="space-y-6">
            {/* Step 1 */}
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-brand-green/20 border-2 border-brand-green flex items-center justify-center">
                <span className="text-brand-green font-bold">1</span>
              </div>
              <div className="flex-1 bg-brand-black-lighter rounded-lg border border-brand-green/30 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-5 h-5 text-brand-green" />
                  <h5 className="font-semibold text-white">Analyse de la Cible</h5>
                </div>
                <p className="text-sm text-gray-400">
                  Le LLM analyse les informations de la cible (IP, domaine, résultats de scan préliminaires) 
                  et génère un profil de risque initial.
                </p>
              </div>
            </div>
            
            {/* Arrow */}
            <div className="flex justify-center">
              <div className="w-1 h-8 bg-gradient-to-b from-brand-green/50 to-brand-green/20"></div>
            </div>
            
            {/* Step 2 */}
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-brand-green/20 border-2 border-brand-green flex items-center justify-center">
                <span className="text-brand-green font-bold">2</span>
              </div>
              <div className="flex-1 bg-brand-black-lighter rounded-lg border border-brand-green/30 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Brain className="w-5 h-5 text-brand-green" />
                  <h5 className="font-semibold text-white">Raisonnement & Planification</h5>
                </div>
                <p className="text-sm text-gray-400 mb-2">
                  Chain-of-Thought: L'IA décompose le problème et génère un plan d'attaque étape par étape.
                </p>
                <div className="bg-brand-black rounded p-3 border border-brand-green/20 text-xs font-mono text-gray-300">
                  <span className="text-brand-green">Pensée IA:</span> "Cible = serveur web (port 80/443). 
                  <br/>→ Étape 1: Scanner technologies (WhatWeb, Wappalyzer)
                  <br/>→ Étape 2: Énumérer répertoires (Gobuster)
                  <br/>→ Étape 3: Chercher vulnérabilités connues (Nuclei, Nikto)"
                </div>
              </div>
            </div>
            
            {/* Arrow */}
            <div className="flex justify-center">
              <div className="w-1 h-8 bg-gradient-to-b from-brand-green/50 to-brand-green/20"></div>
            </div>
            
            {/* Step 3 */}
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-brand-green/20 border-2 border-brand-green flex items-center justify-center">
                <span className="text-brand-green font-bold">3</span>
              </div>
              <div className="flex-1 bg-brand-black-lighter rounded-lg border border-brand-green/30 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-5 h-5 text-brand-green" />
                  <h5 className="font-semibold text-white">Exécution des Outils</h5>
                </div>
                <p className="text-sm text-gray-400">
                  Les outils sélectionnés sont exécutés (séquentiellement ou en parallèle selon config). 
                  Sortie brute capturée pour analyse.
                </p>
                <div className="mt-2 flex gap-2">
                  <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs border border-blue-500/30">Nmap</span>
                  <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs border border-purple-500/30">Nikto</span>
                  <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs border border-green-500/30">Nuclei</span>
                </div>
              </div>
            </div>
            
            {/* Arrow */}
            <div className="flex justify-center">
              <div className="w-1 h-8 bg-gradient-to-b from-brand-green/50 to-brand-green/20"></div>
            </div>
            
            {/* Step 4 */}
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-brand-green/20 border-2 border-brand-green flex items-center justify-center">
                <span className="text-brand-green font-bold">4</span>
              </div>
              <div className="flex-1 bg-brand-black-lighter rounded-lg border border-brand-green/30 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                  <h5 className="font-semibold text-white">Analyse des Résultats</h5>
                </div>
                <p className="text-sm text-gray-400">
                  Le LLM parse les outputs, identifie les vulnérabilités, évalue leur criticité (CVSS) 
                  et propose des exploits potentiels.
                </p>
              </div>
            </div>
            
            {/* Arrow */}
            <div className="flex justify-center">
              <div className="w-1 h-8 bg-gradient-to-b from-brand-green/50 to-brand-green/20"></div>
            </div>
            
            {/* Step 5 - Decision Point */}
            <div className="relative">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-yellow-500/20 border-2 border-yellow-500 flex items-center justify-center">
                  <span className="text-yellow-400 font-bold">5</span>
                </div>
                <div className="flex-1 bg-gradient-to-r from-yellow-500/10 to-transparent rounded-lg border border-yellow-500/30 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-5 h-5 text-yellow-400" />
                    <h5 className="font-semibold text-white">Point de Décision</h5>
                  </div>
                  <p className="text-sm text-gray-400 mb-3">
                    Réflexion: L'IA évalue si elle doit continuer, pivoter vers une autre approche, ou demander validation humaine.
                  </p>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="bg-brand-black rounded p-2 border border-green-500/30 text-center">
                      <p className="text-green-400 font-semibold">✓ Continuer</p>
                      <p className="text-gray-500">Confiance élevée</p>
                    </div>
                    <div className="bg-brand-black rounded p-2 border border-blue-500/30 text-center">
                      <p className="text-blue-400 font-semibold">↻ Pivoter</p>
                      <p className="text-gray-500">Impasse détectée</p>
                    </div>
                    <div className="bg-brand-black rounded p-2 border border-orange-500/30 text-center">
                      <p className="text-orange-400 font-semibold">⚠ Demander</p>
                      <p className="text-gray-500">Risque élevé</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Arrow */}
            <div className="flex justify-center">
              <div className="w-1 h-8 bg-gradient-to-b from-brand-green/50 to-brand-green/20"></div>
            </div>
            
            {/* Step 6 */}
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-brand-green/20 border-2 border-brand-green flex items-center justify-center">
                <span className="text-brand-green font-bold">6</span>
              </div>
              <div className="flex-1 bg-brand-black-lighter rounded-lg border border-brand-green/30 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <RefreshCw className="w-5 h-5 text-brand-green" />
                  <h5 className="font-semibold text-white">Itération & Raffinement</h5>
                </div>
                <p className="text-sm text-gray-400">
                  Retour à l'étape 2 avec nouvelles informations jusqu'à atteindre l'objectif ou le max d'itérations.
                  Self-Reflection: L'IA apprend de ses erreurs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Core LLM Configuration */}
      <div className="bg-brand-black rounded-xl border border-brand-green/30 p-6">
        <h4 className="text-lg font-semibold text-brand-green mb-4 flex items-center gap-2">
          <Cpu className="w-5 h-5" />
          Configuration Modèles LLM
        </h4>
        
        {/* Models Loading State */}
        {modelsLoading && (
          <div className="flex items-center gap-2 text-gray-400 mb-4">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Chargement des modèles SYLink AI...</span>
          </div>
        )}

        {modelsError && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 flex items-center justify-between">
            <span className="text-red-400 text-sm">{modelsError}</span>
            <button
              onClick={fetchOllamaModels}
              className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" />
              Réessayer
            </button>
          </div>
        )}

        {ollamaModels.length > 0 && (
          <div className="bg-brand-green/10 border border-brand-green/30 rounded-lg p-3 mb-4">
            <span className="text-brand-green text-sm">
              {ollamaModels.length} modèle(s) SYLink AI disponible(s)
            </span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Modèle de Raisonnement
              <span className="text-xs text-gray-500 ml-2">(Chain-of-Thought)</span>
            </label>
            <select
              value={config.reasoning_model}
              onChange={(e) => setConfig({...config, reasoning_model: e.target.value})}
              className="w-full bg-brand-black-lighter border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-brand-green/50"
              disabled={modelsLoading || ollamaModels.length === 0}
            >
              {ollamaModels.length === 0 && !modelsLoading && (
                <option value="">Aucun modèle disponible</option>
              )}
              {ollamaModels.map(model => (
                <option key={model.id} value={model.id}>
                  {maskAIModel(model.name)} ({model.size}) - {model.role}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Modèle d'Action
              <span className="text-xs text-gray-500 ml-2">(Exécution rapide)</span>
            </label>
            <select
              value={config.action_model}
              onChange={(e) => setConfig({...config, action_model: e.target.value})}
              className="w-full bg-brand-black-lighter border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-brand-green/50"
              disabled={modelsLoading || ollamaModels.length === 0}
            >
              {ollamaModels.length === 0 && !modelsLoading && (
                <option value="">Aucun modèle disponible</option>
              )}
              {ollamaModels.map(model => (
                <option key={model.id} value={model.id}>
                  {maskAIModel(model.name)} ({model.size}) - {model.role}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="mt-4 grid grid-cols-2 gap-4">
          <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-700 hover:border-brand-green/50 transition-colors">
            <input
              type="checkbox"
              checked={config.enable_chain_of_thought}
              onChange={(e) => setConfig({...config, enable_chain_of_thought: e.target.checked})}
              className="w-5 h-5 rounded border-gray-700 text-brand-green focus:ring-brand-green"
            />
            <div>
              <span className="text-sm font-medium text-white">Chain-of-Thought</span>
              <p className="text-xs text-gray-500">Raisonnement étape par étape visible</p>
            </div>
          </label>
          
          <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-700 hover:border-brand-green/50 transition-colors">
            <input
              type="checkbox"
              checked={config.enable_self_reflection}
              onChange={(e) => setConfig({...config, enable_self_reflection: e.target.checked})}
              className="w-5 h-5 rounded border-gray-700 text-brand-green focus:ring-brand-green"
            />
            <div>
              <span className="text-sm font-medium text-white">Self-Reflection</span>
              <p className="text-xs text-gray-500">L'IA critique ses propres décisions</p>
            </div>
          </label>
        </div>
      </div>

      {/* Execution Configuration */}
      <div className="bg-brand-black rounded-xl border border-brand-green/30 p-6">
        <h4 className="text-lg font-semibold text-brand-green mb-4">Configuration d'Exécution</h4>
        
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Max Itérations: {config.max_iterations}
            </label>
            <input
              type="range"
              min="5"
              max="50"
              value={config.max_iterations}
              onChange={(e) => setConfig({...config, max_iterations: parseInt(e.target.value)})}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
            />
            <p className="text-xs text-gray-500 mt-1">Nombre de cycles raisonnement-action max</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Timeout par outil (sec): {config.timeout_per_tool}
            </label>
            <input
              type="range"
              min="60"
              max="600"
              step="30"
              value={config.timeout_per_tool}
              onChange={(e) => setConfig({...config, timeout_per_tool: parseInt(e.target.value)})}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Stratégie d'outils</label>
            <select
              value={config.tool_selection}
              onChange={(e) => setConfig({...config, tool_selection: e.target.value})}
              className="w-full bg-brand-black-lighter border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-brand-green/50"
            >
              <option value="sequential">Séquentiel (un par un)</option>
              <option value="adaptive">Adaptatif (selon contexte)</option>
              <option value="aggressive">Agressif (parallèle max)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Seuil de risque</label>
            <select
              value={config.risk_threshold}
              onChange={(e) => setConfig({...config, risk_threshold: e.target.value})}
              className="w-full bg-brand-black-lighter border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-brand-green/50"
            >
              <option value="low">Bas (tous exploits autorisés)</option>
              <option value="medium">Moyen (équilibré)</option>
              <option value="high">Élevé (prudent)</option>
            </select>
          </div>
        </div>
        
        <div className="mt-4 space-y-2">
          <label className="flex items-center gap-3 cursor-pointer text-gray-300 hover:text-brand-green transition-colors">
            <input
              type="checkbox"
              checked={config.parallel_execution}
              onChange={(e) => setConfig({...config, parallel_execution: e.target.checked})}
              className="w-4 h-4 rounded border-gray-700 text-brand-green focus:ring-brand-green"
            />
            <span className="text-sm">Exécution parallèle (jusqu'à {config.max_parallel_tools} outils simultanés)</span>
          </label>
          
          <label className="flex items-center gap-3 cursor-pointer text-gray-300 hover:text-brand-green transition-colors">
            <input
              type="checkbox"
              checked={config.prefer_passive_tools}
              onChange={(e) => setConfig({...config, prefer_passive_tools: e.target.checked})}
              className="w-4 h-4 rounded border-gray-700 text-brand-green focus:ring-brand-green"
            />
            <span className="text-sm">Préférer outils passifs (OSINT, énumération)</span>
          </label>
          
          <label className="flex items-center gap-3 cursor-pointer text-gray-300 hover:text-brand-green transition-colors">
            <input
              type="checkbox"
              checked={config.fallback_on_failure}
              onChange={(e) => setConfig({...config, fallback_on_failure: e.target.checked})}
              className="w-4 h-4 rounded border-gray-700 text-brand-green focus:ring-brand-green"
            />
            <span className="text-sm">Fallback automatique si outil échoue</span>
          </label>
          
          <label className="flex items-center gap-3 cursor-pointer text-gray-300 hover:text-brand-green transition-colors">
            <input
              type="checkbox"
              checked={config.require_human_approval}
              onChange={(e) => setConfig({...config, require_human_approval: e.target.checked})}
              className="w-4 h-4 rounded border-gray-700 text-brand-green focus:ring-brand-green"
            />
            <span className="text-sm">Demander approbation humaine avant exploitation</span>
          </label>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="text-2xl">💡</div>
          <div className="text-sm text-gray-300">
            <p className="font-medium text-blue-400 mb-2">Recommandations pour Pentests Réels</p>
            <ul className="space-y-1 text-gray-400 text-xs">
              <li>• <strong>Raisonnement:</strong> Utilisez SYLink AI pour analyse complexe et planification stratégique</li>
              <li>• <strong>Action:</strong> SYLink AI pour exécution rapide des commandes</li>
              <li>• <strong>Chain-of-Thought:</strong> Toujours activé pour traçabilité et debugging</li>
              <li>• <strong>Itérations:</strong> 10-20 pour scans standards, 30-50 pour Red Team avancé</li>
              <li>• <strong>Approval:</strong> Obligatoire en production, optionnel en lab</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LLMLogicConfig;
